/* ═══════════════════════════════════════════════════════════════
   ALTERLY — 2D Transform Node
   Complete 2D affine transform on GPU: translate, scale, rotate & anchor.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';
import { getMediaDimensions } from '../../rendering/media';

const TRANSFORM2D_FRAGMENT_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform vec2 u_translation;
uniform vec2 u_scale;
uniform float u_rotationRad;
uniform vec2 u_anchor;

void main() {
  vec2 pixelPos = v_uv * u_resolution;

  // 1. Shift by translation
  vec2 pos = pixelPos - u_translation;

  // 2. Shift relative to anchor point
  vec2 anchorPix = u_anchor * u_resolution;
  pos -= anchorPix;

  // 3. Inverse rotation
  float c = cos(-u_rotationRad);
  float s = sin(-u_rotationRad);
  pos = vec2(pos.x * c - pos.y * s, pos.x * s + pos.y * c);

  // 4. Inverse scale
  pos /= max(u_scale, vec2(0.001));

  // 5. Restore anchor
  pos += anchorPix;

  vec2 uv = pos / u_resolution;

  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    fragColor = vec4(0.0);
    return;
  }

  fragColor = texture(u_image, uv);
}
`;

export const transform2dNode: NodeDefinition = {
  type: 'transform.2d',
  category: 'Transform',
  label: 'Transform 2D',
  description: 'Full 2D affine transformation: translation, scaling, rotation, and anchor',
  keywords: ['transform', 'move', 'scale', 'rotate', 'anchor', 'translate', 'position'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Transformed', type: 'image' },
  ],
  params: [
    {
      id: 'posX',
      name: 'Position X',
      type: 'number',
      defaultValue: 0,
      min: -2000,
      max: 2000,
      step: 1,
      unit: 'px',
    },
    {
      id: 'posY',
      name: 'Position Y',
      type: 'number',
      defaultValue: 0,
      min: -2000,
      max: 2000,
      step: 1,
      unit: 'px',
    },
    {
      id: 'scaleX',
      name: 'Scale X',
      type: 'slider',
      defaultValue: 100,
      min: 1,
      max: 500,
      step: 1,
      unit: '%',
    },
    {
      id: 'scaleY',
      name: 'Scale Y',
      type: 'slider',
      defaultValue: 100,
      min: 1,
      max: 500,
      step: 1,
      unit: '%',
    },
    {
      id: 'rotation',
      name: 'Rotation',
      type: 'angle',
      defaultValue: 0,
      min: 0,
      max: 360,
      step: 1,
      unit: '°',
      uiHint: 'angle',
    },
    {
      id: 'anchorX',
      name: 'Anchor X',
      type: 'slider',
      defaultValue: 50,
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
    {
      id: 'anchorY',
      name: 'Anchor Y',
      type: 'slider',
      defaultValue: 50,
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: TRANSFORM2D_FRAGMENT_SHADER,
    getUniforms: (inputs, params, _dimensions) => {
      const srcDim = getMediaDimensions(inputs.image);
      const posX = Number(params.posX) || 0;
      const posY = Number(params.posY) || 0;
      const scaleX = (Number(params.scaleX) || 100) / 100;
      const scaleY = (Number(params.scaleY) || 100) / 100;
      const rotDeg = Number(params.rotation) || 0;
      const anchorX = (Number(params.anchorX) || 50) / 100;
      const anchorY = (Number(params.anchorY) || 50) / 100;

      return {
        u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
        u_resolution: [srcDim.width, srcDim.height],
        u_translation: [posX, posY],
        u_scale: [scaleX, scaleY],
        u_rotationRad: (rotDeg * Math.PI) / 180,
        u_anchor: [anchorX, anchorY],
      };
    },
  }),
};
